import React, { useState, useEffect, useRef, useCallback } from "react";
import bannerImg from "../assets/images/House5.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faEnvelope,
  faLocation,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import video from "../assets/images/1026290987-preview.mp4";
import video2 from "../assets/images/1096898873-preview.mp4";
import formattingFunctions from "../functions/formatting";
import houseInterior from "../assets/images/shutterstock_2304013113-min.jpg";
// import houseInterior from "../assets/images/stock-photo-luxury-hotel-bathroom-interior-with-bathtub-and-side-table-concrete-floor-bathing-space-in-modern-2467634645.jpg"

import houseInterior2 from "../assets/images/shutterstock_1935730303-min.jpg";
import skyline from "../assets/images/shutterstock_2467634645-min.jpg";
import condos from "../assets/images/shutterstock_379744201-min.jpg";
import condosLarge from "../assets/images/shutterstock_379744201-min-large.jpg";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import { faMap } from "@fortawesome/free-regular-svg-icons";
import Contact from "./contact.component";

// Custom hooks
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024 // Default fallback size
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onWindowResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  return windowSize;
};

const useScrollVisibility = () => {
  const isScrolledIntoView = useCallback((elem) => {
    if (typeof window === "undefined") return false;

    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  const isScrolledIntoViewTop = useCallback((elem) => {
    if (typeof window === "undefined") return false;

    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      return elemTop - window.innerHeight < 0;
    }
    return false;
  }, []);

  return { isScrolledIntoView, isScrolledIntoViewTop };
};

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const windowSize = useWindowSize();
  const { isScrolledIntoView, isScrolledIntoViewTop } = useScrollVisibility();

  // Refs
  const contact = useRef();
  const loaded = useRef(false);
  const loadedRef = useRef(false);
  const locationRef = useRef(null);
  const agentsRef = useRef(null);
  const slideUpElements = useRef([]);
  const slideInElements = useRef([]);
  const slideRightElements = useRef([]);

  // State
  const [listings, setListings] = useState([]);
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  // Add elements to refs
  const addToSlideUpRefs = (el) => {
    if (el && !slideUpElements.current.includes(el)) {
      slideUpElements.current.push(el);
    }
  };

  const addToSlideInRefs = (el) => {
    if (el && !slideInElements.current.includes(el)) {
      slideInElements.current.push(el);
    }
  };

  const addToSlideRightRefs = (el) => {
    if (el && !slideRightElements.current.includes(el)) {
      slideRightElements.current.push(el);
    }
  };

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (agentsRef.current && isScrolledIntoViewTop(agentsRef.current)) {
        const rect = agentsRef.current.getBoundingClientRect();
        const elemBottom = rect.bottom;
        setOffset(0 - Math.floor(elemBottom * 0.5));
      }

      slideInElements.current.forEach((el) => {
        if (isScrolledIntoView(el)) {
          el?.classList.remove("slide-left");
        }
      });

      if (locationRef.current && isScrolledIntoViewTop(locationRef.current)) {
        let interval = 0;
        slideUpElements.current.forEach((el) => {
          setTimeout(() => {
            el?.classList.remove("mt-10");
          }, interval);
          interval += 250;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrolledIntoView, isScrolledIntoViewTop]);

  // Load effect
  useEffect(() => {
    const handleLoad = () => {
      let interval = 0;
      slideRightElements.current.reverse().forEach((el) => {
        setTimeout(() => {
          el?.classList.remove("-ml-10");
        }, interval);
        interval += 250;
      });
    };

    window.addEventListener("load", handleLoad);
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  async function fetchJson(url, body) {
    let data =
      constants.ENVIRONMENT === "production"
        ? await fetch(url)
        : await fetch(`http://${constants.URL}/metaGet`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            "Content-Type": "application/json",
          },
        });

    if (!data.ok) {
      throw new Error("Failed to fetch data");
    }

    return data.json();
  }

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=((ListAgentKey eq '${constants.AGENT_ID}') or (CoListAgentKey eq '${constants.AGENT_ID}') or (ListOfficeKey eq '${constants.OFFICE_ID}')) and (StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    var fallbackBody = {
      url: `https://query.ampre.ca/odata/Property?$filter=(StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=((ListAgentKey(S)eq(S)%27${constants.AGENT_ID}%27)(S)or(S)(CoListAgentKey(S)eq(S)%27${constants.AGENT_ID}%27)(S)or(S)(ListOfficeKey(S)eq(S)%27${constants.OFFICE_ID}%27))(S)and(S)(StandardStatus(S)eq(S)%27Active%27)(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;
        let fallbackRequest = `odata/Property(Q)$filter=(StandardStatus(S)eq(S)%27Active%27)(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;

        const primaryUrl = `https://${constants.DOMAIN}/api/${request}`;
        const fallbackUrl = `https://${constants.DOMAIN}/api/${fallbackRequest}`;

        let res = await fetchJson(primaryUrl, body);
        if (!res?.value?.length) {
          res = await fetchJson(fallbackUrl, fallbackBody);
        }
        resolve(res);
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "local") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />
      <div>
        <div className="w-full h-[90vh] relative flex justify-center items-start xl:mt-32">
          <div className="absolute inset-0 text-white z-10 px-4 md:px-12 [text-shadow:2px_2px_2px_rgba(0,0,0,0.6)] flex flex-col justify-between">
            <div className="uppercase py-48 xl:py-32 text-center">
              <h1
                style={{ fontWeight: "400" }}
                className="libre text-unset text-5xl sm:text-6xl xl:text-8xl mb-5"
              >
                Alex Pham
              </h1>
              <h2
                style={{ fontWeight: "300" }}
                className="libre text-unset text-3xl sm:text-4xl xl:text-5xl tracking-[7.5px]"
              >
                Real Estate
              </h2>
            </div>
            <div className="flex flex-wrap md:flex-nowrap justify-center gap-6 pt-10 pl-2 md:pl-4 pb-14">
              <Link to={"/about"}>
                <button
                  // onClick={() => contact.current?.scrollIntoView()}
                  className="btn-primary uppercase min-w-[17rem] py-3 px-10 lg:py-5 lg:px-20 text-lg md:text-xl tracking-widest"
                >
                  Discover More
                </button>
              </Link>
              <Link to={"/listings"}>
                <button className="btn-tertiary uppercase min-w-[17rem] py-3 px-10 lg:py-5 lg:px-20 text-lg md:text-xl tracking-widest">Browse Listings</button>
              </Link>
            </div>
          </div>
          <video
            className="h-full w-full object-cover absolute inset-0"
            autoPlay={true}
            muted
            loop
          >
            <source src={constants.HOME_VIDEO} type="video/mp4" />
          </video>
        </div>


        <div className="bg-logo-second w-full">
          <div className="flex text-white justify-between items-center pt-24  px-5 md:px-10 gap-x-5 max-w-[1700px] mx-auto">
            <h1
              ref={addToSlideInRefs}
              className={`text-3xl md:text-4xl font-semibold uppercase text-white tracking-wider libre ease-in-out duration-2000 slide-in ${windowSize > 700 ? "slide-left" : ""
                }`}
            >
              Featured Listings
            </h1>
            <Link
              className="underline font-semibold underline-offset-4 whitespace-nowrap "
              to={"/listings"}
            >
              View All
            </Link>
          </div>

          <div
            style={{ flexFlow: "column wrap" }}
            className="flex gap-5 max-w-[1700px]  min-[1000px]:h-[1350px] min-[1600px]:h-[900px] px-10 mx-auto"
          >
            {listings?.slice(0, 6)?.map((prop) => (
              <Link
                className={`${windowSize >= 1600
                  ? "l-mosaic"
                  : windowSize >= 1000
                    ? "m-mosaic"
                    : "h-[400px]"
                  }  `}
                to={`/listing/${prop.ListingKey}`}
              >
                <div className="relative hover:bg-black overflow-hidden group w-full h-full">
                  <img
                    className="w-full h-full absolute object-cover  group-hover:opacity-50  duration-1000 group-hover:scale-105"
                    src={
                      formatted[prop.ListingKey]?.find((img) => img.Order === 0)
                        ?.MediaURL ??
                      formatted[prop.ListingKey]?.[0]?.MediaURL ??
                      placeholder
                    }
                  />
                  <div className="absolute flex flex-col w-full h-full z-50 overlay py-4 justify-end gap-y-2">
                    <p
                      style={{ fontWeight: "200" }}
                      className="libre min-[900px]:text-[1.25em] text-[1.25em] text-white px-4"
                    >
                      <FontAwesomeIcon icon={faLocationDot} className="pr-3" />
                      {prop?.StreetNumber} {prop?.StreetName}{" "}
                      {prop?.StreetSuffix}
                    </p>

                    <p className="font-bold text-white text-2xl px-4">
                      {prop.CityRegion ?? prop.CrossStreet}
                    </p>
                    <hr className="border-logo-main border-2 duration-1000 w-[20%] group-hover:w-[50%] mx-4 my-2"></hr>
                    <div className=" whitespace-nowrap flex-wrap child:px-4  flex ">
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>${prop.ListPrice?.toLocaleString()}</span>
                      </div>
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>{prop.BedroomsTotal} Beds</span>
                      </div>
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>{prop.BathroomsTotalInteger} Baths</span>
                      </div>
                      <div className=" text-white text-2xl">
                        {prop?.LivingAreaRange ? prop.LivingAreaRange : "--"}{" "}
                        Sqft
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div
            className="px-5 md:px-10 max-w-[1700px] mx-auto pb-16 md:pb-24 pt-12 md:pt-16
             grid grid-cols-1 min-[900px]:grid-cols-[40%_60%] gap-10 md:gap-12"
          >
            {/* Image Section */}
            <div className="flex items-start min-[900px]:justify-start">
              <div className="max-[450px]:max-w-[100%] md:max-w-[70%] lg:max-w-[30vw] aspect-square overflow-hidden rounded-r-full">
                <img
                  src={constants.HEADSHOT2}
                  className="h-full w-full object-cover"
                  alt={constants.AGENT_NAME}
                />
              </div>
            </div>


            {/* Content Section */}
            <div className="flex flex-col text-white">
              <h1
                ref={addToSlideInRefs}
                className={`text-3xl md:text-4xl uppercase text-white pb-6 tracking-wider font-semibold libre ease-in-out duration-2000 slide-in ${windowSize > 700 ? "slide-left" : ""
                  }`}
              >
                About Me
              </h1>

              {constants.ABOUT ? (
                constants.ABOUT.split("<|>").map((p, index) => (
                  <p
                    key={index}
                    className="text-base md:text-lg lg:text-2xl tracking-wide leading-relaxed mb-5 pr-2 md:pr-8 lg:pr-16 xl:pr-24"
                  >
                    {index === 0 ? (
                      <span className="text-2xl md:text-3xl lg:text-4xl font-semibold">
                        {p}
                      </span>
                    ) : (
                      p
                    )}
                  </p>
                ))
              ) : (
                <p>Coming Soon</p>
              )}

              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <Link
                  className="btn-primary bg-transparent self-start"
                  to="/about"
                >
                  <button className="uppercase tracking-widest">
                    Why Choose {constants.AGENT_NAME.split(" ")[0]}
                  </button>
                </Link>

                <Link
                  className="btn-secondary self-start"
                  to="/contact"
                >
                  <button className="uppercase tracking-widest">Contact</button>
                </Link>
              </div>
            </div>
          </div>

          <div className="flex flex-col text-white justify-center items-center pb-16 px-5 md:px-10 gap-x-5 max-w-[1800px] mx-auto gap-y-5">
            <h1
              ref={addToSlideInRefs}
              className={`text-3xl md:text-4xl pt-12 libre tracking-wider font-semibold ease-in-out duration-2000 uppercase slide-in ${windowSize > 700 ? "slide-left" : ""
                }`}
            >
              Client Testimonials
            </h1>

            <Swiper
              className="w-full max-w-[1200px]"
              slidesPerView={1}
              pagination={{
                clickable: true,
              }}
              navigation={true}
              loop={true}
              loopPreventsSliding={true}
              modules={[Pagination, Autoplay, Navigation]}
              style={{
                "--swiper-pagination-color": "#b87e58",
                "--swiper-navigation-color": "#b87e58",
              }}
            >
              {constants.TESTIMONIALS.slice(0, 5)?.map((t, index) => (
                <SwiperSlide key={index}>
                  <div className="flex flex-col items-center justify-center text-center min-h-[200px] md:h-[30vh] w-full max-w-[1000px] mx-auto px-5 md:px-10 py-6 md:py-12 gap-6">
                    <span className="text-white text-2xl italic tracking-wide">
                      "{t.quote}"
                    </span>

                    <span className="font-semibold text-white italic font-bold text-2xl tracking-wide">- {t.name}</span>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <Link to={"/testimonials"} className="btn-secondary mt-6 uppercase tracking-widest ">More Testimonials
            </Link>
          </div>
        </div>

        <div className="w-full">
          <div className="max-w-[1700px] mx-auto px-5 md:px-10 text-white pb-10">
            <div className="w-full py-16">
              <div
                style={{
                  backgroundImage: `url(${condosLarge})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundAttachment: "fixed",
                }}
                className="min-h-[80vh] md:min-h-[600px] relative  "
              >
                <div
                  style={{ backgroundColor: "rgb(0, 0, 0, .5)" }}
                  className="absolute h-full w-full z-10"
                ></div>
                <div className="h-full w-full text-white absolute z-20 text-center flex justify-center flex-col items-center gap-10">
                  <h1
                    style={{ fontWeight: "300" }}
                    className="text-3xl sm:text-4xl md:text-5xl px-3 libre tracking-wider"
                  >
                    Which Neighbourhood Fits Your Style?
                  </h1>
                  <p className="text-2xl font-light px-10 max-w-[1400px] tracking-wider">
                    {constants.NEIGHBOURHOOD_DESCRIPTION}
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                gridTemplateColumns: `repeat( auto-fit, minmax(${windowSize > 600 ? "300px" : "300px"
                  }, 1fr) )`,
              }}
              className="grid gap-10 justify-center flex-wrap "
            >
              {constants.LOCATIONS.slice(0, 4).map((location, index) => (
                <Link
                  key={location.name}
                  to={`location/${formattingFunctions.slugify(location.name)}`}
                >
                  <div
                    ref={index === 0 ? locationRef : null}
                    className={`w-full aspect-[3/4] slide-up duration-1000 ease-in-out relative mt-10 cursor-pointer overflow-hidden flex items-center justify-center bg-black group`}
                  >
                    <img
                      className=" h-full  object-cover opacity-75 hover:opacity-100 w-full  group-hover:scale-105 duration-1000 "
                      src={location.img}
                    />
                    {/* <h1 className="text-3xl  text-white py-8 libre px-10  z-10">{location.name}</h1> */}
                    <div className=" px-4 py-2 duration-1000 h-[120px] bg-logo-second absolute bottom-0 w-full text-white group-hover:h-[130px]">
                      <div className="h-12 w-12 group-hover:scale-110 duration-1000 rounded-full bg-white text-logo-second flex items-center justify-center absolute top-[-20%] right-4">
                        <FontAwesomeIcon
                          className="fa-xl"
                          icon={faLocationDot}
                        />
                      </div>
                      <p className="text-2xl libre font-semibold pb-3">
                        {location.name}
                      </p>
                      <p>{location.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="w-full bg-logo-second">
            <div className="px-10 mx-auto max-w-[1700px] text-white">
              <h1
                ref={addToSlideInRefs}
                className={`text-3xl md:text-4xl tracking-wider font-semibold py-12 libre ease-in-out duration-2000 uppercase slide-in ${windowSize > 700 ? "slide-left" : ""
                  }`}
              >
                Useful Tools
              </h1>

              <div className="grid md:grid-cols-3 gap-10 child:overflow-hidden  pb-10">
                <Link className="group" to={"/calculator"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={calc}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl pt-2 pb-2 tracking-wider">Mortgage Calculator</h3>
                  <span className="text-xl tracking-wide">
                    Find a price range that works with your budget by using our
                    Mortgage Calculator
                  </span>
                </Link>
                <Link className="group" to={"/real-estate-reports"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={report}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl tracking-wider pt-2 pb-2 ">Real Estate Reports</h3>
                  <span className="text-xl tracking-wide">
                    Gain valuable insights into the market with our free Real
                    Estate Reports
                  </span>
                </Link>
                <Link className="group" to={"/evaluation"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={house}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl tracking-wider pt-2 pb-2 ">Home Evaluation</h3>
                  <span className="text-xl tracking-wide">
                    How much is your home worth in today's market? Find out with
                    our free Home Evaluation
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <Contact />
      </div>
    </>
  );
}
